/**
 * `entities/taxonomy` mutations.
 *
 * Every mutation invalidates the list; detail-scoped mutations also
 * invalidate the affected detail + change-log. Toasts are surfaced here so
 * every taxonomy write gives consistent feedback.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { i18n } from '@/shared/i18n';
import { enqueueSnackbar } from 'notistack';
import { assetsHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { track } from '@/shared/analytics';
import { UnreadableWriteError, parseResponse, parseWriteResponse } from '@/lib/api/parse-response';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import type {
  AddTaxonomyVersionPayload,
  CreateTaxonomyPayload,
  SetTaxonomyVersionStatusPayload,
  SuggestAbbreviationsPayload,
  Taxonomy,
  UpdateTaxonomyDraftPayload,
} from '../model/types';
import { TAXONOMY_QUERY_KEYS } from './query-keys';
import {
  abbreviationSuggestionsSchema,
  bulkDeleteTaxonomiesSchema,
  taxonomySchema,
  taxonomyVersionSchema,
} from './schemas';

const TAXONOMIES_URL = '/api/v1/taxonomies';

export function useCreateTaxonomy(options?: { onSuccess?: (created: Taxonomy) => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateTaxonomyPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: TAXONOMIES_URL,
        body: payload,
      });
      return parseWriteResponse(taxonomySchema, response, () => {
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      });
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      track('taxonomy_schema_created', { schema_id: data.id, scope: data.scope });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.schemaCreated'), {
        variant: 'success',
      });
      options?.onSuccess?.(data);
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedCreateSchema'), {
        variant: 'error',
      });
    },
  });
}

export function useAddTaxonomyVersion(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AddTaxonomyVersionPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}/versions`,
        path: { id },
        body: payload,
      });
      return parseWriteResponse(taxonomyVersionSchema, response, () => {
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) });
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      });
    },
    onSuccess: (_version, payload) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      // `parentVersionId` is only sent when branching off something other than
      // the head, which is exactly what a restore is.
      track('taxonomy_schema_version_created', {
        schema_id: id,
        is_restore: !!payload.parentVersionId,
      });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.newVersionCreated'), {
        variant: 'success',
      });
    },
    onError: (error) => {
      // The version was created and the refetch brings it to the builder.
      if (error instanceof UnreadableWriteError) return;
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedCreateVersion'), {
        variant: 'error',
      });
    },
  });
}

/**
 * In-place draft autosave. Silent on success (no toast — saves fire on a
 * debounce as the user edits); surfaces a toast only on failure. Invalidates
 * the detail + list so the head summary stays in sync.
 */
export function useUpdateTaxonomyDraft(id: string, versionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: UpdateTaxonomyDraftPayload) => {
      const response = await assetsHttp.patch({
        url: `${TAXONOMIES_URL}/{id}/versions/{versionId}`,
        path: { id, versionId },
        body: payload,
        // `throwOnError` so the AxiosError — and with it the STATUS — reaches the
        // caller. Without it the client resolves with `data: undefined` and the
        // old `throw new Error('Failed to save changes')` discarded the status,
        // which meant a 409 was indistinguishable from a network failure and the
        // builder couldn't tell a concurrent edit from a broken connection.
        throwOnError: true,
        // The concurrent-edit case is explained by a persistent banner in the
        // builder; the interceptor's own persistent toast would be a second copy
        // of the same news, once per rejected autosave.
        // eslint-disable-next-line i18next/no-literal-string -- HTTP header value
        headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
      });
      return parseWriteResponse(taxonomySchema, response, () => {
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) });
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      });
    },
    onSuccess: (updated) => {
      // Seed the DETAIL cache with the server's own response.
      //
      // Without this, autosave looked like it worked and then silently lost
      // the edit: the PATCH persisted fine, but the detail query still held
      // the pre-edit snapshot. The global staleTime is 5 MINUTES, so
      // navigating to the list and straight back re-mounted the builder,
      // served that stale snapshot, and — because it was still "fresh" — did
      // NOT refetch. Fields the user had just added appeared to vanish until
      // a hard reload. The header said "All changes saved" the whole time,
      // and it was telling the truth.
      //
      // `setQueryData`, not `invalidateQueries`: the PATCH returns the same
      // `TaxonomyResponseDto` the detail query does, so we already have the
      // truth. Invalidating would refetch on every debounce tick and could
      // stomp the builder's in-flight local edits.
      queryClient.setQueryData(TAXONOMY_QUERY_KEYS.detail(id), updated);
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      // The change log is NOT invalidated: `updateDraftVersion` writes no entry
      // for a draft save (see its docblock), so refetching it on every debounced
      // autosave was a request that could never return anything new.
    },
    onError: (error) => {
      // A 409 is a concurrent edit, not a failure to reach the server: the
      // builder shows a banner explaining it and offering to reload, so a toast
      // here would just be a second, vaguer version of the same news.
      if (parseProblemDetail(error, '').status === 409) return;
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedSaveChanges'), {
        variant: 'error',
      });
    },
  });
}

export function useSetTaxonomyVersionStatus(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: SetTaxonomyVersionStatusPayload & { versionId: string }) => {
      const { versionId, ...body } = variables;
      const response = await assetsHttp.patch({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}/versions/{versionId}/status`,
        path: { id, versionId },
        body,
      });
      return parseWriteResponse(taxonomyVersionSchema, response, () => {
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) });
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      });
    },
    onSuccess: (version) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      // One event with a status enum rather than three near-identical ones:
      // publish, unpublish and archive are the same decision at different
      // points, and a single series makes the funnel readable.
      track('taxonomy_schema_status_changed', {
        schema_id: id,
        status: version.status,
        field_count: version.fields.length,
      });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.statusUpdated'), {
        variant: 'success',
      });
    },
    onError: (error) => {
      // A 409 here is a refusal that NAMES its remedy — which list has no
      // published version, or which schema pins this one. The response
      // interceptor already shows that text in a persistent toast, so a generic
      // one on top of it is a second, vaguer copy of the same news.
      if (parseProblemDetail(error, '').status === 409) return;
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedUpdateStatus'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Request an LLM-generated pack of code abbreviations that shrink the generated
 * name under a channel's character budget while keeping each field's codes
 * unique. Read-only (no cache invalidation) — the caller applies chosen changes
 * through the normal draft autosave.
 */
export function useSuggestAbbreviations(id: string) {
  return useMutation({
    mutationFn: async (payload: SuggestAbbreviationsPayload) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}/abbreviation-suggestions`,
        path: { id },
        body: payload,
      });
      return parseResponse(abbreviationSuggestionsSchema, response);
    },
    onSuccess: (result) => {
      track('taxonomy_abbreviations_suggested', {
        schema_id: id,
        fits_target: result.fitsTarget,
        shortfall: result.shortfall,
        suggestion_count: result.suggestions.length,
        unlink_count: result.unlinkChanges.length,
      });
    },
    onError: () => {
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyMutations.couldNotGenerateSuggestionsPlease'),
        { variant: 'error' },
      );
    },
  });
}

export function useDeleteTaxonomy(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { reason: string }) => {
      await assetsHttp.delete({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}`,
        path: { id },
        body: variables,
      });
    },
    onSuccess: () => {
      // Purge the dead schema's own queries before invalidating the index —
      // a blanket invalidate refetches a mounted detail/change-log and 404s on
      // what was a successful delete.
      queryClient.removeQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) });
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      track('taxonomy_schema_deleted', { schema_id: id });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.schemaDeleted'), {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedDeleteSchema'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Copies a schema WITH its fields and rules.
 *
 * Server-side: the previous client-side duplicate posted only
 * name/scope/market/platform to the create endpoint, so duplicating a
 * 32-field schema produced an empty one.
 */
export function useDuplicateTaxonomy(options?: { onSuccess?: (created: Taxonomy) => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/{id}/duplicate`,
        path: { id },
      });
      return parseWriteResponse(taxonomySchema, response, () => {
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      });
    },
    onSuccess: (created, sourceId) => {
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      track('taxonomy_schema_duplicated', { source_schema_id: sourceId });
      enqueueSnackbar(
        i18n.t('knowledge:taxonomy.taxonomyMutations.createdNamed', { name: created.name }),
        {
          variant: 'success',
        },
      );
      options?.onSuccess?.(created);
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedDuplicateSchema'), {
        variant: 'error',
      });
    },
  });
}

/**
 * Deletes several schemas under one justification.
 *
 * No in-use guard needed — nothing binds TO a schema — so unlike the list
 * equivalent this is all-or-nothing rather than partial.
 */
export function useBulkDeleteTaxonomies(options?: { onSuccess?: () => void }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: { ids: string[]; reason: string }) => {
      const response = await assetsHttp.post({
        throwOnError: true,
        url: `${TAXONOMIES_URL}/bulk-delete`,
        body: variables,
      });
      return parseWriteResponse(bulkDeleteTaxonomiesSchema, response, () => {
        void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      });
    },
    onSuccess: (result, variables) => {
      // Purge each dead schema's detail before the index invalidate, else a
      // mounted query refetches and 404s.
      variables.ids.forEach((id) =>
        queryClient.removeQueries({ queryKey: TAXONOMY_QUERY_KEYS.detail(id) }),
      );
      void queryClient.invalidateQueries({ queryKey: TAXONOMY_QUERY_KEYS.list() });
      const n = result.deletedIds.length;
      // The server's count, not the requested one: a batch can be partial.
      track('taxonomy_schemas_bulk_deleted', { count: n });
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.deletedSchemas', { count: n }), {
        variant: 'success',
      });
      options?.onSuccess?.();
    },
    onError: () => {
      enqueueSnackbar(i18n.t('knowledge:taxonomy.taxonomyMutations.failedDeleteSchemas'), {
        variant: 'error',
      });
    },
  });
}
